// Block-based email signature builder types

export interface BaseBlock {
  id: string;
  type: string;
}

export interface ImageBlock extends BaseBlock {
  type: 'image';
  src: string;
  width: number;
  height?: number;
  borderRadius?: number;
  link?: string;
}

export interface TextBlock extends BaseBlock {
  type: 'text';
  content: string; // Supports {{dynamic_fields}}
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  color: string;
  fontFamily: string;
  textAlign?: 'left' | 'center' | 'right';
}

export interface ContactRowBlock extends BaseBlock {
  type: 'contact';
  icon: 'email' | 'phone' | 'whatsapp' | 'website' | 'location' | 'custom';
  customIconUrl?: string;
  label: string; // Supports {{dynamic_fields}}
  iconColor: string;
  iconSize: number;
  fontSize: number;
  textColor: string;
  linkType: 'email' | 'phone' | 'url' | 'none';
}

export interface SocialIconsBlock extends BaseBlock {
  type: 'social';
  icons: SocialIcon[];
  iconSize: number;
  iconStyle: 'color' | 'monochrome' | 'circle';
  spacing: number;
}

export interface SocialIcon {
  platform: 'facebook' | 'instagram' | 'linkedin' | 'twitter' | 'youtube' | 'tiktok' | 'whatsapp';
  url: string;
}

export interface DividerBlock extends BaseBlock {
  type: 'divider';
  orientation: 'horizontal' | 'vertical';
  thickness: number;
  color: string;
  length: number;
}

export interface SpacerBlock extends BaseBlock {
  type: 'spacer';
  height: number;
}

export interface LayoutBlock extends BaseBlock {
  type: 'layout';
  columns: 1 | 2;
  gap: number;
  verticalAlign: 'top' | 'middle' | 'bottom';
  children: SignatureBlock[][]; // Array of columns, each with blocks
}

export type SignatureBlock = 
  | ImageBlock 
  | TextBlock 
  | ContactRowBlock 
  | SocialIconsBlock 
  | DividerBlock 
  | SpacerBlock 
  | LayoutBlock;

export interface SignatureData {
  blocks: SignatureBlock[];
  version: number;
}

// Dynamic field data interface for signature preview
export interface SignatureDynamicData {
  user?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    personal_phone_number?: string;
    company_position?: string;
  };
  company?: {
    name?: string;
    email?: string;
    company_phone_number?: string;
    website?: string;
  };
}

// Block creation helpers
export const createId = (): string => {
  return Math.random().toString(36).substring(2, 11);
};

export const createImageBlock = (overrides?: Partial<ImageBlock>): ImageBlock => ({
  id: createId(),
  type: 'image',
  src: '',
  width: 120,
  height: undefined,
  borderRadius: 0,
  link: undefined,
  ...overrides,
});

export const createTextBlock = (overrides?: Partial<TextBlock>): TextBlock => ({
  id: createId(),
  type: 'text',
  content: '',
  fontSize: 14,
  fontWeight: 'normal',
  color: '#000000',
  fontFamily: 'Arial, sans-serif',
  textAlign: 'left',
  ...overrides,
});

export const createContactRowBlock = (overrides?: Partial<ContactRowBlock>): ContactRowBlock => ({
  id: createId(),
  type: 'contact',
  icon: 'email',
  label: '',
  iconColor: '#666666',
  iconSize: 16,
  fontSize: 13,
  textColor: '#333333',
  linkType: 'none',
  ...overrides,
});

export const createSocialIconsBlock = (overrides?: Partial<SocialIconsBlock>): SocialIconsBlock => ({
  id: createId(),
  type: 'social',
  icons: [],
  iconSize: 24,
  iconStyle: 'color',
  spacing: 8,
  ...overrides,
});

export const createDividerBlock = (overrides?: Partial<DividerBlock>): DividerBlock => ({
  id: createId(),
  type: 'divider',
  orientation: 'horizontal',
  thickness: 1,
  color: '#cccccc',
  length: 100,
  ...overrides,
});

export const createSpacerBlock = (overrides?: Partial<SpacerBlock>): SpacerBlock => ({
  id: createId(),
  type: 'spacer',
  height: 16,
  ...overrides,
});

export const createLayoutBlock = (overrides?: Partial<LayoutBlock>): LayoutBlock => ({
  id: createId(),
  type: 'layout',
  columns: 2,
  gap: 16,
  verticalAlign: 'top',
  children: [[], []],
  ...overrides,
});
